import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useApi } from '../../../../../shared/hooks/useApi';
import CustomTable from '../../../../../shared/components/table/custom-table';
import { LoaderComponent } from '../../../../../shared/components/loaders/page-loader';
import { fetchProductComponentDetails } from './input-product-apis';

// Row interface
interface ProductRow {
  shipmentId: string;
  productId: string;
  tcId: string;
  inputTcId: string;
}

interface RenderDetailPanelProps {
  row: { original: ProductRow };
}

// Column definitions
const standardColumns = [
  {
    accessorKey: 'inputTcProductStandardLabelGrade',
    header: 'Standard Label Grade',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'inputTcProductCertifiedWeight',
    header: 'Certified Weight (kg)',
    minSize: 60,
    maxSize: 150,
  },
  // {
  //   accessorKey: 'inputTcProductCertifiedWeightUsed',
  //   header: 'Certified Weight Used (kg)',
  //   minSize: 60,
  //   maxSize: 150,
  // },
  { accessorKey: 'inputTcProductStandardLabelGradeDescription', 
    header: 'Standard Label Grade Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

const rawMaterialColumns = [
  {
    accessorKey: 'inputTcProductRawMaterialCode',
    header: 'Raw Material Code',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'inputTcProductRawMaterialPercentage',
    header: 'Raw Material Percentage',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'inputTcProductRawMaterialCodeDescription', 
    header: 'Raw Material Description', 
    minSize: 60, 
    maxSize: 150 
  },
];

const ipRawMaterialColumns = [
  {
    accessorKey: 'inputTcCertifiedRawMaterialCode',
    header: 'Input Certified Raw Material Code',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'outputTcCertifiedRawMaterialCode',
    header: 'Output Certified Raw Material Code',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'inputTcCertifiedRawMaterialCodeDescription',
    header: 'Input Certified Raw Material Description',
    minSize: 60,
    maxSize: 150,
  },
  {
    accessorKey: 'outputTcCertifiedRawMaterialCodeDescription',
    header: 'Output Certified Raw Material Description',
    minSize: 60,
    maxSize: 150,
  },
  { accessorKey: 'inputTcCertifiedRawMaterialWeightUsed', 
    header: 'Input Certified Raw Material Weight Used (kg)', 
    minSize: 60, 
    maxSize: 150 
  },
];

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  const { shipmentId, productId, tcId, inputTcId } = row.original;

  const { loading, executeGet } = useApi();
  const [productDetails, setProductDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!shipmentId || !productId || !tcId || !inputTcId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    fetchProductComponentDetails(executeGet, tcId, shipmentId, productId, inputTcId)
      .then((data) => setProductDetails(data?.data))
      .catch((err) => setError(err.message));
  }, [shipmentId, productId, tcId, inputTcId, executeGet]);

  if (loading) {
    return <LoaderComponent />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!productDetails) {
    return <Typography>Loading product details...</Typography>;
  }

  /** ======================
   * DATA EXTRACTION & MAPPING
   ========================*/

  const inputTcInformation = productDetails.inputTcInformation || [];
  let standardInfo = [];
  let rawMaterialInfo = [];
  let ipRawMaterialInfo = [];

  if (inputTcInformation.length > 0) {
    const productInfo = inputTcInformation[0]?.inputTcProductInformation?.[0];

    // Extract Standard Info
    standardInfo = productInfo?.inputTcProductStandardInformation || [];

    // Extract Raw Material Info
    rawMaterialInfo = productInfo?.inputProductRawMaterialInformation || [];

    ipRawMaterialInfo = inputTcInformation[0]?.inputTcCertifiedRawMaterialWeightUsedInformation || [];
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      {/* Product Component Standard Info */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Product Component Standard Info
        </Typography>
        <CustomTable
          columns={standardColumns}
          data={standardInfo}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />
      {/* Raw Material Info */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Raw Material Info
        </Typography>
        <CustomTable
          columns={rawMaterialColumns}
          data={rawMaterialInfo}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider/>
      
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
          Input Certified Raw Material Info
        </Typography>
        <CustomTable
          columns={ipRawMaterialColumns}
          data={ipRawMaterialInfo}
          enableTopToolbar={false}
          enableBottomToolbar={false}
        />
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;
